/* hero-slideshow.js — the editorial hero slideshow (sections/hero.liquid).
 *
 * Loaded ONLY when the section has more than one slide. With one slide the
 * hero is static markup and this file is never requested.
 *
 * The accessibility contract, which is the part worth reading:
 *
 *  - prefers-reduced-motion:reduce means NO auto-advance at all, not a faster
 *    fade. The pause/play button is hidden for those visitors too, because a
 *    control for something that is not happening is a lie. The query is
 *    watched live, so toggling the OS setting takes effect without a reload.
 *  - WCAG 2.2.2 (Pause, Stop, Hide) needs a real mechanism to stop moving
 *    content. Hover and focus pausing are courtesy: a touch user hovers
 *    nothing, and a reader who never tabs into the hero can never reach the
 *    focus pause. Hence a labelled button.
 *  - The live region is written to ONLY when a PERSON moved the slideshow.
 *    Announcing every auto-advance is exactly how an auto-rotating hero traps
 *    a screen-reader user: the region interrupts whatever they are reading,
 *    every few seconds, forever.
 *  - Any deliberate move (arrow, index, keyboard) stops rotation for the rest
 *    of the page view. The visitor took control; taking it back off them a few
 *    seconds later is the single most hostile thing a carousel does.
 *  - Rotation also pauses while the tab is hidden and while the hero is
 *    scrolled out of view, so nobody comes back to a slide they never saw.
 *  - A horizontal swipe is a deliberate move (Luke, 2026-09-12, from his
 *    phone on the live theme: "I should also be able to swipe left and right
 *    to flip between slides"). It goes through the same took() as an arrow:
 *    it ends rotation and it announces. The gesture is decided on release,
 *    never during the drag — see the swipe block for why nothing follows the
 *    finger and why a vertical scroll that starts on the hero keeps scrolling.
 *
 * Loading: slide 1 has real src/srcset and is the LCP image; every other slide
 * is rendered with data-src/data-srcset and costs zero bytes until promoted.
 * We promote the slide being shown and warm exactly one ahead, so paging never
 * waits on a cold fetch but an idle visitor never pays for slide 4.
 */
(() => {
  const MQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const reduced = () => !!(MQ && MQ.matches);

  function init(root) {
    if (root.dataset.hsReady) return;
    const copies = [...root.querySelectorAll('[data-hs-copy]')];
    const medias = [...root.querySelectorAll('[data-hs-media]')];
    const dots = [...root.querySelectorAll('[data-hs-index]')];
    const n = copies.length;
    if (n < 2) return;
    root.dataset.hsReady = '1';

    const live = root.querySelector('[data-hs-live]');
    const toggle = root.querySelector('[data-hs-toggle]');
    const interval = Math.max(0, parseInt(root.dataset.autoplay, 10) || 0);

    let cur = 0;
    let timer = null;
    let remain = interval;
    let startedAt = 0;
    let stopped = false;          // the visitor (or reduced motion) ended rotation
    const holds = new Set();      // transient pauses: hover, focus, tab, viewport

    // ---- image promotion ---------------------------------------------------
    // <source> before <img>: querySelectorAll is in document order and inside a
    // <picture> the source precedes the img, so the picture resolves to the
    // right candidate the moment the img gets its srcset.
    const promote = (i) => {
      const fig = medias[i];
      if (!fig || fig.dataset.hsLoaded) return;
      fig.dataset.hsLoaded = '1';
      fig.querySelectorAll('[data-srcset],[data-src]').forEach((el) => {
        if (el.dataset.srcset) { el.srcset = el.dataset.srcset; delete el.dataset.srcset; }
        if (el.dataset.src) { el.src = el.dataset.src; delete el.dataset.src; }
      });
    };

    // ---- movement ----------------------------------------------------------
    const go = (i, announce) => {
      const to = ((i % n) + n) % n;
      promote(to);
      promote((to + 1) % n);
      copies.forEach((el, k) => el.classList.toggle('is-active', k === to));
      medias.forEach((el, k) => el.classList.toggle('is-active', k === to));
      dots.forEach((b, k) => b.setAttribute('aria-current', k === to ? 'true' : 'false'));
      cur = to;
      remain = interval;
      if (announce && live) {
        const title = copies[to].dataset.hsTitle || '';
        live.textContent = `Slide ${to + 1} of ${n}${title ? `: ${title}` : ''}`;
      }
    };

    // ---- the clock ---------------------------------------------------------
    // setTimeout with an explicit remainder rather than setInterval, so a pause
    // resumes where it stopped. That has to be true or the index rule's fill —
    // which is a CSS animation paused with animation-play-state — would drift
    // out of step with the actual advance, and the progress bar would be lying.
    const arm = () => {
      clearTimeout(timer);
      startedAt = Date.now();
      timer = setTimeout(() => { go(cur + 1, false); arm(); }, remain);
    };

    const running = () => !stopped && !holds.size && interval > 0 && !reduced();

    const sync = () => {
      root.classList.toggle('is-stopped', stopped || reduced() || interval === 0);
      root.classList.toggle('is-playing', !stopped && interval > 0 && !reduced());
      root.classList.toggle('is-paused', holds.size > 0);
      if (toggle) {
        const playing = !stopped;
        toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
        toggle.hidden = reduced() || interval === 0;
      }
      if (running()) { if (!timer) arm(); }
      else if (timer) { clearTimeout(timer); timer = null; }
    };

    const hold = (why) => {
      if (holds.has(why)) return;
      if (!holds.size && timer) remain = Math.max(0, remain - (Date.now() - startedAt));
      holds.add(why);
      sync();
    };
    const release = (why) => { if (holds.delete(why)) sync(); };

    // A deliberate move ends rotation for this page view.
    const took = (i) => { stopped = true; go(i, true); sync(); };

    // ---- wiring ------------------------------------------------------------
    const prev = root.querySelector('[data-hs-prev]');
    const next = root.querySelector('[data-hs-next]');
    if (prev) prev.addEventListener('click', () => took(cur - 1));
    if (next) next.addEventListener('click', () => took(cur + 1));
    dots.forEach((b, k) => b.addEventListener('click', () => took(k)));
    if (toggle) {
      toggle.addEventListener('click', () => {
        stopped = !stopped;
        if (!stopped) remain = interval;
        sync();
        // The button's own state change is worth announcing; the slide is not
        // changing, so go()'s announcement would be wrong here.
        if (live) live.textContent = stopped ? 'Slideshow paused' : 'Slideshow playing';
      });
    }

    root.addEventListener('mouseenter', () => hold('hover'));
    root.addEventListener('mouseleave', () => release('hover'));
    root.addEventListener('focusin', () => hold('focus'));
    root.addEventListener('focusout', (e) => {
      if (!root.contains(e.relatedTarget)) release('focus');
    });

    root.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      // never steal an arrow key from a field, however unlikely one is here
      const t = e.target;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      e.preventDefault();
      took(cur + (e.key === 'ArrowRight' ? 1 : -1));
      (e.key === 'ArrowRight' ? next : prev)?.focus?.();
    });

    // ---- swipe ---------------------------------------------------------------
    // Pointer events, not touch events, so a trackpad or mouse drag works for
    // free; and decided at pointerup, not tracked during the move:
    //
    //  - The slideshow is a cross-dissolve, not a track. There is no strip of
    //    slides to pull along, so "following the finger" would mean inventing
    //    one — and a track that can be dragged past its last slide is exactly
    //    the horizontal overflow scripts/overflow-gate.mjs exists to catch.
    //    Nothing here moves during the drag; the slide changes on release.
    //  - touch-action:pan-y on the section (theme.css) hands vertical panning
    //    to the browser BEFORE any script runs: a scroll that starts on the
    //    hero scrolls the page natively and this handler sees pointercancel,
    //    never a swipe. That is the mistake most carousels make — reading the
    //    finger themselves and eating the page scroll — and it is avoided by
    //    the CSS, not by the threshold. The threshold below is for pointers
    //    the browser does not pan for (mouse, pen).
    //  - The threshold is 40px AND more horizontal than vertical. 40 is above
    //    a trembling tap (Android's slop is 8, iOS's ~10) and below the ~60px
    //    a thumb covers in a lazy flick at 390 wide; the angle test makes a
    //    diagonal fling read as the page scroll it almost certainly was.
    //  - No pointer capture. Capturing retargets pointerup to the section,
    //    and a click's target is the nearest common ancestor of down and up —
    //    so a plain tap on a CTA would stop hitting the link. The up and
    //    cancel listeners go on the window instead, once, while a drag is in
    //    flight, so a mouse released outside the hero still ends it cleanly.
    //  - A mouse drag that swiped must not ALSO click whatever it started on
    //    (touch browsers cancel that click themselves; mouse ones do not).
    //    A capture-phase click guard swallows the click that follows the
    //    release, and retires itself the same turn.
    //  - Controls are excluded at pointerdown: a drag that starts on the pause
    //    button is somebody fumbling the button, not paging.
    //  - prefers-reduced-motion needs nothing here. The swipe goes through
    //    the same go() as an arrow, and critical.css already clamps every
    //    transition to .001ms for those visitors, so the slide still changes
    //    — instantly.
    const SWIPE_MIN = 40;
    let drag = null;
    const onUp = (e) => {
      const d = drag;
      if (!d || e.pointerId !== d.id) return;
      endDrag();
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
      if (d.type === 'mouse' || d.type === 'pen') {
        // The click, if any, is dispatched in this same task right after
        // pointerup; the timeout retires the guard before the next one, so a
        // release that produced no click (outside the hero, or on a different
        // element from the press) cannot leave it lying in wait.
        const swallow = (c) => { c.preventDefault(); c.stopPropagation(); };
        root.addEventListener('click', swallow, true);
        setTimeout(() => root.removeEventListener('click', swallow, true), 0);
      }
      took(cur + (dx < 0 ? 1 : -1));
    };
    const onCancel = (e) => { if (drag && e.pointerId === drag.id) endDrag(); };
    const onMove = (e) => {
      const d = drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      // Once a MOUSE drag is clearly sideways, stop it selecting the copy as
      // it goes. Only then: the heading stays selectable for everyone who is
      // not swiping. Touch never reaches here — the browser already decided.
      if (!d.side && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        d.side = true;
        if (d.type !== 'touch') {
          root.classList.add('is-dragging');
          const sel = window.getSelection && window.getSelection();
          if (sel && sel.rangeCount) sel.removeAllRanges();
        }
      }
    };
    const endDrag = () => {
      drag = null;
      root.classList.remove('is-dragging');
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('pointermove', onMove);
    };
    // An <img> and an <a> are natively draggable: a mouse press-and-move on
    // either starts an HTML drag-and-drop, the pointer stream is cancelled
    // and pointerup never comes. The photograph and the CTAs are exactly
    // where a mouse swipe starts. Cancelling dragstart only while a swipe is
    // in flight leaves drag-and-drop alone everywhere else.
    root.addEventListener('dragstart', (e) => { if (drag) e.preventDefault(); });
    root.addEventListener('pointerdown', (e) => {
      if (!e.isPrimary || e.button !== 0 || drag) return;
      const t = e.target;
      if (t && t.closest && t.closest('button,input,select,textarea')) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, type: e.pointerType, side: false };
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      window.addEventListener('pointermove', onMove);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) hold('tab'); else release('tab');
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((es) => {
        es.forEach((en) => { if (en.isIntersecting) release('viewport'); else hold('viewport'); });
      }, { threshold: 0.25 }).observe(root);
    }

    if (MQ) {
      const onMQ = () => sync();
      if (MQ.addEventListener) MQ.addEventListener('change', onMQ);
      else if (MQ.addListener) MQ.addListener(onMQ);
    }

    // Theme editor: selecting a slide block should show that slide, and hold
    // rotation while staff are working on it. Without this, editing slide 3
    // means watching slide 1 and wondering why nothing you type appears.
    document.addEventListener('shopify:block:select', (e) => {
      if (!root.contains(e.target)) return;
      const i = copies.indexOf(e.target.closest('[data-hs-copy]'));
      if (i >= 0) go(i, false);
      hold('editor');
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      if (root.contains(e.target)) release('editor');
    });

    sync();
  }

  const boot = () => document.querySelectorAll('[data-hero-slideshow]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
